import axios from 'axios'
import type { AxiosInstance, AxiosResponse, AxiosError, AxiosRequestConfig, CancelTokenSource } from 'axios'
import { useAuthStore } from '@/stores/auth'
import router from '@/router'
import type {
  ApiResponse,
  NetworkError,
  NetworkErrorType,
  ApiClientConfig,
  RequestConfig
} from '@/types/api'



/**
 * 请求重试配置接口
 */
interface RetryConfig {
  maxRetries: number
  retryDelay: number
  retryCondition: (error: AxiosError) => boolean
}

/**
 * 请求队列项接口
 */
interface QueuedRequest {
  resolve: (value: any) => void
  reject: (error: any) => void
  config: AxiosRequestConfig
}

/**
 * 请求缓存项接口
 */
interface RequestCacheItem {
  promise: Promise<any>
  timestamp: number
  cancelToken: CancelTokenSource
}

/**
 * API 客户端类
 * 提供标准化的 HTTP 请求服务，包含认证、错误处理、令牌刷新、重试机制和请求去重功能
 */
class ApiClient {
  private instance: AxiosInstance
  private isRefreshing = false
  private failedQueue: QueuedRequest[] = []
  private requestCache = new Map<string, RequestCacheItem>()
  private retryConfig: RetryConfig
  private readonly CACHE_DURATION = 5000 // 5秒内的相同请求会被去重

  constructor(config: Partial<ApiClientConfig> = {}) {
    
    const defaultConfig: ApiClientConfig = {
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 10000,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      withCredentials: false
    }

    const finalConfig = { ...defaultConfig, ...config }

    // 配置重试策略
    this.retryConfig = {
      maxRetries: 3,
      retryDelay: 1000,
      retryCondition: (error: AxiosError) => {
        // 只对网络错误和 5xx 服务器错误进行重试
        return !error.response ||
          error.code === 'ECONNABORTED' ||
          error.code === 'ENOTFOUND' ||
          error.code === 'ECONNRESET' ||
          (error.response.status >= 500 && error.response.status < 600)
      }
    }

    this.instance = axios.create(finalConfig)
    this.setupInterceptors()
    this.setupCacheCleanup()
  }

  /**
   * 设置缓存清理定时器
   */
  private setupCacheCleanup(): void {
    // 每30秒清理一次过期的缓存
    setInterval(() => {
      const now = Date.now()
      for (const [key, item] of this.requestCache.entries()) {
        if (now - item.timestamp > this.CACHE_DURATION) {
          item.cancelToken.cancel('请求缓存过期')
          this.requestCache.delete(key)
        }
      }
    }, 30000)
  }

  /**
   * 生成请求缓存键
   */
  private generateCacheKey(config: AxiosRequestConfig): string {
    const { method = 'get', url = '', params, data } = config
    return `${method.toUpperCase()}:${url}:${JSON.stringify(params || {})}:${JSON.stringify(data || {})}`
  }

  /**
   * 检查是否为可缓存的请求（GET 请求）
   */
  private isCacheableRequest(config: AxiosRequestConfig): boolean {
    return (config.method || 'get').toLowerCase() === 'get'
  }

  /**
   * 检查是否为重要请求（不应该被缓存去重）
   */
  private isImportantRequest(config: AxiosRequestConfig): boolean {
    const url = config.url || ''
    // 用户协议、认证相关的请求、问卷相关请求、余额相关请求、项目相关请求和个人中心相关请求不应该被缓存
    const importantPaths = [
      '/auth/agreement',
      '/auth/login',
      '/auth/register',
      '/auth/me',
      '/auth/refresh',
      '/questionnaire',
      '/balance',
      '/project-types',
      '/projects',
      // 个人中心相关API - 这些请求需要实时数据，不应该被缓存
      '/user/profile/extended',
      '/user/membership',
      '/user/notifications',
      '/user/payment-addresses',
      '/user/projects/statistics',
      '/user/transactions/summary',
      '/user/carbon-footprint',
      // 预售意向相关API - 这些请求需要实时数据，不应该被缓存
      '/presale-intentions',
      '/orders/presale-intention',
      '/orders',
      '/order-record',
      // 资产相关API - 这些请求需要实时数据，不应该被缓存
      '/assets/summary',
      '/assets/breakdown',
      '/assets/history',
      '/balance/asset-history',
      '/user-orders',
      // 碳积分收购相关API - 这些请求需要实时数据，不应该被缓存
      '/buyers',
      '/remaining-carbon-credits',
      '/calculate-sale-impact',
      '/sell-carbon-credits'
    ]
    return importantPaths.some(path => url.includes(path))
  }

  /**
   * 设置请求和响应拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器 - 添加认证令牌和请求去重
    this.instance.interceptors.request.use(
      (config) => {
        const authStore = useAuthStore()
        const token = authStore.token

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // 添加请求时间戳和重试计数用于调试
        if (import.meta.env.DEV) {
          config.metadata = {
            startTime: Date.now(),
            retryCount: config.metadata?.retryCount || 0
          }
        }

        // 请求去重处理（仅对 GET 请求，但排除重要的API）
        if (this.isCacheableRequest(config) && !this.isImportantRequest(config)) {
          const cacheKey = this.generateCacheKey(config)
          const cachedRequest = this.requestCache.get(cacheKey)

          if (cachedRequest) {
            console.log(`[API] 请求去重: ${config.method?.toUpperCase()} ${config.url}`)
            // 取消当前请求，使用缓存的结果
            const cancelError = new Error('使用缓存请求')
            cancelError.name = 'CACHED_REQUEST'
              ; (cancelError as any).__CACHED_REQUEST__ = true
              ; (cancelError as any).promise = cachedRequest.promise
              ; (cancelError as any).config = config
            throw cancelError
          }
        }

        return config
      },
      (error) => {
        // 处理缓存的请求
        if (error.__CACHED_REQUEST__) {
          return error.promise
        }

        console.error('请求拦截器错误:', error)
        return Promise.reject(this.createNetworkError('UNKNOWN_ERROR', '请求配置错误', error))
      }
    )

    // 响应拦截器 - 处理通用错误、令牌刷新和重试机制
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 记录响应时间（开发环境）
        if (import.meta.env.DEV && response.config.metadata?.startTime) {
          const duration = Date.now() - response.config.metadata.startTime
          const retryCount = response.config.metadata?.retryCount || 0
          const retryInfo = retryCount > 0 ? ` (重试 ${retryCount} 次)` : ''
          console.log(`[API] 请求成功 ${response.config.method?.toUpperCase()} ${response.config.url} 耗时: ${duration}ms${retryInfo}`)
        }

        // 清理请求缓存
        if (this.isCacheableRequest(response.config)) {
          const cacheKey = this.generateCacheKey(response.config)
          this.requestCache.delete(cacheKey)
        }

        return response
      },
      async (error: AxiosError) => {
        // 处理缓存请求的特殊情况
        if ((error as any).__CACHED_REQUEST__) {
          return (error as any).promise
        }

        const originalRequest = error.config as AxiosRequestConfig & {
          _retry?: boolean
          _retryCount?: number
          _authRetry?: boolean
        } | undefined

        // 如果没有原始请求配置，直接返回错误
        if (!originalRequest) {
          console.warn('[API] 无法获取原始请求配置，直接返回错误')
          return Promise.reject(this.handleError(error))
        }

        // 清理请求缓存
        if (this.isCacheableRequest(originalRequest)) {
          const cacheKey = this.generateCacheKey(originalRequest)
          this.requestCache.delete(cacheKey)
        }

        // 处理 401 未授权错误 - 改进的令牌刷新逻辑
        if (error.response?.status === 401 && !originalRequest._authRetry) {
          return this.handle401Error(originalRequest, error)
        }

        // 处理网络错误重试
        if (this.shouldRetryRequest(error, originalRequest)) {
          return this.retryRequest(originalRequest, error)
        }

        return Promise.reject(this.handleError(error))
      }
    )
  }

  /**
   * 检查是否为认证相关的请求（登录、注册等）
   */
  private isAuthRequest(config: AxiosRequestConfig): boolean {
    const url = config.url || ''
    const authEndpoints = ['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password']
    return authEndpoints.some(endpoint => url.includes(endpoint))
  }

  /**
   * 检查是否为项目类型密码验证请求
   */
  private isProjectTypePasswordRequest(config: AxiosRequestConfig): boolean {
    const url = config.url || ''
    return url.includes('/project-types/') && url.includes('/verify-password')
  }

  /**
   * 检查是否为令牌刷新请求
   */
  private isRefreshRequest(config: AxiosRequestConfig): boolean {
    const url = config.url || ''
    return url.includes('/auth/refresh')
  }

  /**
   * 处理 401 认证错误
   */
  private async handle401Error(originalRequest: AxiosRequestConfig & { _authRetry?: boolean }, error: AxiosError): Promise<any> {
    console.log('[API] 检测到 401 错误，开始处理认证')



    // 如果是认证请求（登录、注册等）或刷新请求，直接返回错误，不进行令牌刷新处理
    if (this.isAuthRequest(originalRequest) || this.isRefreshRequest(originalRequest)) {
      console.log('[API] Auth/refresh request failed, returning error directly')
      return Promise.reject(this.handleError(error))
    }

    // 如果是项目类型密码验证请求，直接返回错误，不进行令牌刷新处理
    if (this.isProjectTypePasswordRequest(originalRequest)) {
      console.log('[API] 项目类型密码验证失败，直接返回错误，不触发登出')
      return Promise.reject(this.handleError(error))
    }

    // 检查错误详情，区分令牌过期和无效令牌
    const errorData = error.response?.data as any
    const isTokenExpired = errorData?.error_code === 'TOKEN_EXPIRED' ||
      (errorData?.message && errorData.message.includes('expired')) ||
      (errorData?.message && errorData.message.includes('过期'))

    if (!isTokenExpired) {
      // 如果不是令牌过期，而是无效令牌，直接清除认证状态
      console.log('[API] 检测到无效令牌，清除认证状态')
      const authStore = useAuthStore()
      authStore.logout()

      // 只有在不是登录页面时才重定向
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login')
      }

      return Promise.reject(this.createNetworkError('AUTH_ERROR', '认证令牌无效，请重新登录'))
    }

    // 处理令牌过期的情况
    if (this.isRefreshing) {
      // 如果正在刷新令牌，将请求加入队列
      console.log('[API] 令牌刷新中，将请求加入队列')
      return new Promise((resolve, reject) => {
        this.failedQueue.push({
          resolve,
          reject,
          config: originalRequest
        })
      })
    }

    originalRequest._authRetry = true
    this.isRefreshing = true

    try {
      console.log('[API] 开始刷新令牌')
      const authStore = useAuthStore()
      const refreshed = await authStore.refreshToken()

      if (refreshed) {
        console.log('[API] 令牌刷新成功，处理队列中的请求')
        // 处理队列中的请求
        this.processQueue(null)
        // 重新发送原始请求
        return this.instance(originalRequest)
      } else {
        throw new Error('令牌刷新失败')
      }
    } catch (refreshError) {
      console.error('[API] 令牌刷新失败:', refreshError)
      // 刷新失败，清除认证状态并重定向到登录页
      this.processQueue(refreshError)
      const authStore = useAuthStore()
      authStore.logout()

      // 只有在不是登录页面时才重定向
      if (router.currentRoute.value.path !== '/login') {
        router.push('/login')
      }

      return Promise.reject(this.createNetworkError('AUTH_ERROR', '认证失败，请重新登录'))
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * 判断是否应该重试请求
   */
  private shouldRetryRequest(error: AxiosError, config?: AxiosRequestConfig & { _retryCount?: number }): boolean {
    if (!config) {
      return false
    }
    const retryCount = config._retryCount || 0
    return retryCount < this.retryConfig.maxRetries &&
      this.retryConfig.retryCondition(error)
  }

  /**
   * 重试请求
   */
  private async retryRequest(config: AxiosRequestConfig & { _retryCount?: number }, error: AxiosError): Promise<any> {
    if (!config) {
      throw new Error('无法重试请求：配置对象为空')
    }

    const retryCount = (config._retryCount || 0) + 1
    const delay = this.retryConfig.retryDelay * Math.pow(2, retryCount - 1) // 指数退避

    console.log(`[API] Request failed, retrying in ${delay}ms (attempt ${retryCount}):`, error.message)

    // 更新重试计数
    config._retryCount = retryCount
    if (config.metadata) {
      config.metadata.retryCount = retryCount
    }

    // 等待指定时间后重试
    await new Promise(resolve => setTimeout(resolve, delay))

    return this.instance(config)
  }

  /**
   * 处理令牌刷新队列
   */
  private processQueue(error: any): void {
    console.log(`[API] 处理队列中的 ${this.failedQueue.length} 个请求`)

    this.failedQueue.forEach(({ resolve, reject, config }) => {
      if (error) {
        reject(error)
      } else {
        // 重新发送请求
        this.instance(config).then(resolve).catch(reject)
      }
    })

    this.failedQueue = []
  }

  /**
   * 处理 HTTP 错误，转换为标准化的网络错误
   */
  private handleError(error: any): NetworkError {
    // 记录错误详情（开发环境）
    if (import.meta.env.DEV) {
      const config = error.config
      const retryCount = config?.metadata?.retryCount || 0
      const retryInfo = retryCount > 0 ? ` (已重试 ${retryCount} 次)` : ''
      console.error(`[API] Request failed ${config?.method?.toUpperCase()} ${config?.url}${retryInfo}:`, error.message)
    }

    // 处理请求取消
    if (axios.isCancel(error)) {
      return this.createNetworkError('CONNECTION_ERROR', '请求已取消')
    }

    // 处理超时错误
    if (error.code === 'ECONNABORTED' || (error.message && error.message.includes('timeout'))) {
      return this.createNetworkError('TIMEOUT', '请求超时，请检查网络连接')
    }

    // 处理网络连接错误
    if (!error.response) {
      let message = '网络连接失败，请检查网络设置'

      if (error.code === 'ENOTFOUND') {
        message = '无法连接到服务器，请检查网络连接'
      } else if (error.code === 'ECONNRESET') {
        message = '网络连接被重置，请稍后重试'
      } else if (error.code === 'ECONNREFUSED') {
        message = '服务器拒绝连接，请稍后重试'
      }

      return this.createNetworkError('CONNECTION_ERROR', message)
    }

    const { status, data } = error.response

    switch (status) {
      case 400:
        return this.createNetworkError('VALIDATION_ERROR', '请求参数错误', status, data)
      case 401:
        // 401 错误的详细处理
        const errorData = data as any
        let message = '认证失败，请重新登录'

        if (errorData?.error_code === 'TOKEN_EXPIRED') {
          message = '登录已过期，请重新登录'
        } else if (errorData?.error_code === 'TOKEN_INVALID') {
          message = '登录令牌无效，请重新登录'
        } else if (errorData?.message) {
          message = errorData.message
        }

        return this.createNetworkError('AUTH_ERROR', message, status, data)
      case 403:
        return this.createNetworkError('AUTH_ERROR', '权限不足，无法访问该资源', status, data)
      case 404:
        return this.createNetworkError('SERVER_ERROR', '请求的资源不存在', status, data)
      case 422:
        const validationData = data as any
        let validationMessage = '数据验证失败'

        if (validationData?.message) {
          validationMessage = validationData.message
        } else if (validationData?.errors) {
          // 提取第一个验证错误信息
          const firstError = Object.values(validationData.errors)[0]
          if (Array.isArray(firstError) && firstError.length > 0) {
            validationMessage = firstError[0] as string
          }
        }

        return this.createNetworkError('VALIDATION_ERROR', validationMessage, status, data)
      case 429:
        return this.createNetworkError('SERVER_ERROR', '请求过于频繁，请稍后再试', status, data)
      case 500:
        return this.createNetworkError('SERVER_ERROR', '服务器内部错误，请稍后再试', status, data)
      case 502:
        return this.createNetworkError('SERVER_ERROR', '网关错误，请稍后再试', status, data)
      case 503:
        return this.createNetworkError('SERVER_ERROR', '服务暂时不可用，请稍后再试', status, data)
      case 504:
        return this.createNetworkError('SERVER_ERROR', '网关超时，请稍后再试', status, data)
      default:
        return this.createNetworkError('UNKNOWN_ERROR', `Request failed (${status})`, status, data)
    }
  }

  /**
   * 创建标准化的网络错误对象
   */
  private createNetworkError(
    type: NetworkErrorType,
    message: string,
    status?: number,
    details?: any
  ): NetworkError {
    return {
      type,
      message,
      status,
      details,
      code: details?.error_code || details?.code
    }
  }

  /**
   * 执行请求并处理缓存
   */
  private async executeRequest<T>(
    requestFn: () => Promise<AxiosResponse<ApiResponse<T>>>,
    config: AxiosRequestConfig
  ): Promise<ApiResponse<T>> {
    // 为请求创建取消令牌
    const cancelTokenSource = axios.CancelToken.source()
    const requestConfig = { ...config, cancelToken: cancelTokenSource.token }

    try {
      // 对于 GET 请求，检查缓存
      if (this.isCacheableRequest(requestConfig)) {
        const cacheKey = this.generateCacheKey(requestConfig)
        const cachedRequest = this.requestCache.get(cacheKey)

        if (cachedRequest) {
          console.log(`[API] 使用缓存请求: ${requestConfig.method?.toUpperCase()} ${requestConfig.url}`)
          return await cachedRequest.promise
        }

        // 创建新的请求并缓存
        const requestPromise = requestFn().then(
          response => response.data,
          error => {
            // Clear cache on request failure
            this.requestCache.delete(cacheKey)
            throw error
          }
        )

        this.requestCache.set(cacheKey, {
          promise: requestPromise,
          timestamp: Date.now(),
          cancelToken: cancelTokenSource
        })

        const result = await requestPromise

        // 请求成功后清理缓存
        this.requestCache.delete(cacheKey)
        return result
      } else {
        // 非缓存请求直接执行
        const response = await requestFn()
        return response.data
      }
    } catch (error) {
      // 清理缓存
      if (this.isCacheableRequest(requestConfig)) {
        const cacheKey = this.generateCacheKey(requestConfig)
        this.requestCache.delete(cacheKey)
      }
      throw error
    }
  }

  /**
   * GET 请求
   */
  async get<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    const requestConfig = { ...config, method: 'get', url: endpoint }
    return this.executeRequest<T>(
      () => this.instance.get<ApiResponse<T>>(endpoint, config),
      requestConfig
    )
  }

  /**
   * POST 请求
   */
  async post<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    const requestConfig = { ...config, method: 'post', url: endpoint, data }
    return this.executeRequest<T>(
      () => this.instance.post<ApiResponse<T>>(endpoint, data, config),
      requestConfig
    )
  }

  /**
   * PUT 请求
   */
  async put<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    const requestConfig = { ...config, method: 'put', url: endpoint, data }
    return this.executeRequest<T>(
      () => this.instance.put<ApiResponse<T>>(endpoint, data, config),
      requestConfig
    )
  }

  /**
   * PATCH 请求
   */
  async patch<T>(endpoint: string, data?: any, config?: RequestConfig): Promise<ApiResponse<T>> {
    const requestConfig = { ...config, method: 'patch', url: endpoint, data }
    return this.executeRequest<T>(
      () => this.instance.patch<ApiResponse<T>>(endpoint, data, config),
      requestConfig
    )
  }

  /**
   * DELETE 请求
   */
  async delete<T>(endpoint: string, config?: RequestConfig): Promise<ApiResponse<T>> {
    const requestConfig = { ...config, method: 'delete', url: endpoint }
    return this.executeRequest<T>(
      () => this.instance.delete<ApiResponse<T>>(endpoint, config),
      requestConfig
    )
  }

  /**
   * 取消所有进行中的请求
   */
  cancelAllRequests(reason = '用户取消请求'): void {
    console.log(`[API] 取消所有进行中的请求: ${reason}`)

    for (const [key, item] of this.requestCache.entries()) {
      item.cancelToken.cancel(reason)
      this.requestCache.delete(key)
    }
  }

  /**
   * 取消特定的请求
   */
  cancelRequest(endpoint: string, method = 'GET'): void {
    const cacheKey = `${method.toUpperCase()}:${endpoint}:::`
    const cachedRequest = this.requestCache.get(cacheKey)

    if (cachedRequest) {
      console.log(`[API] 取消请求: ${method.toUpperCase()} ${endpoint}`)
      cachedRequest.cancelToken.cancel('用户取消请求')
      this.requestCache.delete(cacheKey)
    }
  }

  /**
   * 获取当前进行中的请求数量
   */
  getPendingRequestsCount(): number {
    return this.requestCache.size
  }

  /**
   * 获取进行中的请求列表
   */
  getPendingRequests(): string[] {
    return Array.from(this.requestCache.keys())
  }

  /**
   * 更新重试配置
   */
  updateRetryConfig(config: Partial<RetryConfig>): void {
    this.retryConfig = { ...this.retryConfig, ...config }
  }

  /**
   * 获取当前重试配置
   */
  getRetryConfig(): RetryConfig {
    return { ...this.retryConfig }
  }

  /**
   * 清理过期的缓存
   */
  clearExpiredCache(): void {
    const now = Date.now()
    let clearedCount = 0

    for (const [key, item] of this.requestCache.entries()) {
      if (now - item.timestamp > this.CACHE_DURATION) {
        item.cancelToken.cancel('缓存过期')
        this.requestCache.delete(key)
        clearedCount++
      }
    }

    if (clearedCount > 0) {
      console.log(`[API] 清理了 ${clearedCount} 个过期缓存`)
    }
  }

  /**
   * 获取 API 客户端状态信息
   */
  getStatus(): {
    isRefreshing: boolean
    pendingRequests: number
    queuedRequests: number
    cacheSize: number
  } {
    return {
      isRefreshing: this.isRefreshing,
      pendingRequests: this.requestCache.size,
      queuedRequests: this.failedQueue.length,
      cacheSize: this.requestCache.size
    }
  }

  /**
   * 获取原始 axios 实例（用于特殊需求）
   */
  getInstance(): AxiosInstance {
    return this.instance
  }

  /**
   * 更新基础配置
   */
  updateConfig(config: Partial<ApiClientConfig>): void {
    Object.assign(this.instance.defaults, config)
  }

  /**
   * 销毁 API 客户端，清理所有资源
   */
  destroy(): void {
    console.log('[API] 销毁 API 客户端')

    // 取消所有进行中的请求
    this.cancelAllRequests('API 客户端销毁')

    // 清理队列
    this.failedQueue = []

    // 重置状态
    this.isRefreshing = false
  }
}

// 创建默认的 API 客户端实例
const apiClient = new ApiClient()

// 导出默认实例和类
export default apiClient
export { ApiClient, apiClient }